/**
 * NicknameSelectionModal Component
 * Modal for selecting or entering a nickname (V5)
 */

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRegion } from "@/contexts/RegionContext";
import { useToast } from "@/hooks/use-toast";
import { getNicknameSuggestions, setNickname } from "@/lib/nicknameService";
import { cn } from "@/lib/utils";
import { Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { NicknameInput } from "./NicknameInput";

interface NicknameSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  currentNickname?: string;
  required?: boolean; // If true, user must set nickname before closing
}

export function NicknameSelectionModal({
  open,
  onOpenChange,
  onSuccess,
  currentNickname,
  required = false,
}: NicknameSelectionModalProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(
    null
  );
  const [customNickname, setCustomNickname] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const { toast } = useToast();
  const { selectedSuburb, selectedRegion } = useRegion();

  // Get suburb label for suggestions
  const suburbLabel =
    selectedSuburb && selectedSuburb !== "ALL"
      ? selectedSuburb
      : selectedRegion && selectedRegion !== "ALL"
      ? selectedRegion
      : undefined;

  // Load suggestions when modal opens
  // Always load suggestions, even if currentNickname exists (for nickname changes)
  useEffect(() => {
    if (open) {
      setSelectedSuggestion(null);
      setCustomNickname("");
      setShowCustomInput(false);
      loadSuggestions();
    }
  }, [open, suburbLabel]);

  const loadSuggestions = async () => {
    setIsLoading(true);
    try {
      const data = await getNicknameSuggestions(suburbLabel);
      setSuggestions(data);
      setSelectedSuggestion((current) => current || data[0] || null);
    } catch (error: any) {
      toast({
        title: "오류",
        description: error.message || "닉네임 추천을 불러오는데 실패했습니다",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setSelectedSuggestion(suggestion);
    setShowCustomInput(false);
    setCustomNickname("");
  };

  const handleSave = async () => {
    const nicknameToSave = showCustomInput
      ? customNickname.trim()
      : selectedSuggestion;

    if (!nicknameToSave) {
      toast({
        title: "닉네임을 선택하세요",
        description: "추천 닉네임을 선택하거나 직접 입력해주세요",
        variant: "destructive",
      });
      return;
    }

    if (showCustomInput && !isValid) {
      toast({
        title: "올바른 닉네임을 입력하세요",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      await setNickname({
        nickname: nicknameToSave,
        suburb: selectedSuburb !== "ALL" ? selectedSuburb : undefined,
        regionCode: selectedRegion !== "ALL" ? selectedRegion : undefined,
      });

      toast({
        title: "닉네임이 설정되었습니다",
        description: `${nicknameToSave}로 시작하세요!`,
      });

      // Call onSuccess and wait for it to complete (for refetching user info)
      if (onSuccess) {
        await onSuccess();
      }

      // Close modal after refetch is complete
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "닉네임 설정 실패",
        description: error.message || "잠시 후 다시 시도해주세요",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const selectedNickname = showCustomInput
    ? customNickname
    : selectedSuggestion;
  const canSave = selectedNickname && (showCustomInput ? isValid : true);

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        // If required, prevent closing without setting nickname
        if (required && newOpen === false && !currentNickname) {
          toast({
            title: "닉네임 설정이 필요합니다",
            description:
              "안전하고 즐거운 커뮤니티를 위해 닉네임을 설정해주세요.",
            variant: "destructive",
          });
          return;
        }
        onOpenChange(newOpen);
      }}
    >
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => {
          // Prevent closing by clicking outside if required
          if (required && !currentNickname) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing with ESC if required
          if (required && !currentNickname) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="h-6 w-6 text-primary" />
            {required ? "환영합니다! 🎉" : "동네에서 쓸 별명을 정했어요 👋"}
          </DialogTitle>
          <DialogDescription className="text-base space-y-2">
            {required ? (
              <>
                <p className="font-medium text-foreground">
                  소비자의 안전한 거래, 더 재미있고 부담없는 커뮤니티를
                  만들어가요! ✨
                </p>
                <p className="text-muted-foreground">
                  유쾌하고 기쁨을 주는 이름을 만들어보세요.
                  <br />
                  지역 + 형용사 + 호주 동물 이름으로 친구들과 소통해요.
                </p>
              </>
            ) : (
              <>
                <p>실명 대신, 지역 + 형용사 + 호주 동물 형식의 별명을 사용해요.</p>
                <p>마음에 드는 이름을 골라보세요.</p>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current nickname display (if exists) */}
          {currentNickname && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">현재 닉네임</p>
              <p className="text-lg font-semibold">{currentNickname}</p>
            </div>
          )}

          {/* Suggestions */}
          {!showCustomInput && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">추천 네이밍</h3>
                {isLoading && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>

              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <Card key={i} className="p-4 animate-pulse">
                      <div className="h-6 bg-muted rounded" />
                    </Card>
                  ))}
                </div>
              ) : suggestions.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {suggestions.map((suggestion, index) => (
                    <Card
                      key={index}
                      className={cn(
                        "p-4 cursor-pointer transition-all hover:border-primary hover:shadow-md",
                        selectedSuggestion === suggestion &&
                          "border-primary border-2 bg-primary/5"
                      )}
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      <p className="font-medium text-center">{suggestion}</p>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  추천 닉네임을 불러올 수 없습니다
                </p>
              )}

              {/* Custom input button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowCustomInput(true)}
              >
                직접 입력할래요
              </Button>
            </div>
          )}

          {/* Custom input */}
          {showCustomInput && (
            <div className="space-y-4">
              <NicknameInput
                value={customNickname}
                onChange={setCustomNickname}
                onValidationChange={setIsValid}
                placeholder="예: 시드니 깜찍 코알라"
                label="닉네임 직접 입력"
              />
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomNickname("");
                }}
              >
                추천 닉네임 보기
              </Button>
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex gap-2 pt-4">
            {!required && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                나중에
              </Button>
            )}
            <Button
              className="flex-1"
              onClick={handleSave}
              disabled={!canSave || isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  저장 중...
                </>
              ) : (
                "이 별명으로 시작하기"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
